/**
 * Check-ins (CONVENTIONS R10): the snapshot a check-in stores, counted as the forecast counts
 * net worth, and how a stored checkpoint is read back.
 */
import { startOfMonth, type Day } from '../core/calendar.ts';
import { abs, sum, ZERO, type Decimal } from '../core/decimal.ts';
import { debtsCountedInNetWorth, totalDepreciatingAssetValue } from '../models/plan.ts';
import type { BalanceSnapshot, CalcContext, Checkpoint, Id, Plan, Transaction } from '../types/domain.ts';
import { remainingBalance } from './debt-projector.ts';
import { totalPropertyEquity } from './equity.ts';
import { planSaleDates, saleSettlements } from './sales.ts';

export interface CheckInDebt {
  readonly debt: Transaction;
  readonly balance: Decimal;
}

/**
 * Plan.checkInDebts: each debt in the debt line (R3) with its check-in balance as a positive
 * amount, the confirmed balance when given, else what it owes at `date`. A debt its asset's sale
 * has settled by `date` (from the sale month, R2/R9) is left out.
 */
export function checkInDebts(
  plan: Plan,
  confirmed: ReadonlyMap<Id, Decimal>,
  date: Day,
  ctx: CalcContext,
): readonly CheckInDebt[] {
  const debts = debtsCountedInNetWorth(plan);
  const settlements = saleSettlements(planSaleDates(plan), debts);
  return debts
    .filter((debt) => {
      const saleDate = settlements.get(debt.id);
      return saleDate === undefined || date < startOfMonth(saleDate);
    })
    .map((debt) => ({ debt, balance: abs(confirmed.get(debt.id) ?? remainingBalance(debt, date, ctx.now)) }));
}

/**
 * Plan.checkInBalances: the confirmed cash and investments, the check-in debts, property equity
 * and the value of the non-property assets from their depreciation curves (never asked of the
 * user) at `date`.
 */
export function checkInBalances(
  plan: Plan,
  confirmed: { readonly cash: Decimal; readonly investments: Decimal; readonly debtBalances: ReadonlyMap<Id, Decimal> },
  date: Day,
  ctx: CalcContext,
): BalanceSnapshot {
  return {
    cash: confirmed.cash,
    investments: confirmed.investments,
    savings: ZERO,
    debt: sum(checkInDebts(plan, confirmed.debtBalances, date, ctx).map((d) => d.balance)),
    propertyEquity: totalPropertyEquity(plan, date, ctx),
    assetValue: totalDepreciatingAssetValue(plan, date),
  };
}

/** BalanceSnapshot.resolvingAssetValue: an unrecorded asset value is taken from the plan at `date`. */
export const resolvingAssetValue = (snapshot: BalanceSnapshot, plan: Plan, date: Day): BalanceSnapshot =>
  snapshot.assetValue !== null ? snapshot : { ...snapshot, assetValue: totalDepreciatingAssetValue(plan, date) };

/** Checkpoint.resolvingAssetValue: both snapshots resolved against the plan at the checkpoint date. */
export const resolveCheckpoint = (checkpoint: Checkpoint, plan: Plan): Checkpoint => ({
  ...checkpoint,
  confirmedBalances: resolvingAssetValue(checkpoint.confirmedBalances, plan, checkpoint.date),
  forecastedBalances: resolvingAssetValue(checkpoint.forecastedBalances, plan, checkpoint.date),
});
